import { configureRequestParams, getErrorMessage } from '@/utils/helpers'

import axios from './instance'

// Organization (parent-account) endpoints (RSCX-320). Org Owners/Admins manage
// shared templates and view cross-facility data; the backend enforces access.

/**
 * List organizations the current user belongs to (Xverse admins see all).
 *
 * @returns {Promise<object>} Paginated organizations
 */
export const getOrganizations = async (payload = {}) => {
  const { sorting, pagination, searchTerm } = payload
  const params = configureRequestParams(sorting, pagination, searchTerm)
  try {
    return await axios.get(`/organizations/${params}`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Get a single organization by ID.
 *
 * @param {string} organizationId
 * @returns {Promise<object>} The organization
 */
export const getOrganization = async (organizationId) => {
  try {
    return await axios.get(`/organizations/${organizationId}/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Create an organization (Xverse admins only). Optionally seed the owner.
 *
 * @param {object} payload - Organization fields + optional ownerEmail
 * @returns {Promise<object>} The created organization
 */
export const addOrganization = async (payload) => {
  try {
    return await axios.post('/organizations/', payload)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Update an organization.
 *
 * @param {object} payload - Must include `id`
 * @returns {Promise<object>} The updated organization
 */
export const updateOrganization = async ({ id, ...data }) => {
  try {
    return await axios.patch(`/organizations/${id}/`, data)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * List the child facilities of an organization.
 *
 * @param {string} organizationId
 * @returns {Promise<object>} Child facilities (SimpleEntity shape)
 */
export const getOrganizationFacilities = async (organizationId) => {
  try {
    return await axios.get(`/organizations/${organizationId}/facilities/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Cross-facility rollup summary for the org dashboard.
 *
 * @param {string} organizationId
 * @returns {Promise<object>} { totals, facilities[] }
 */
export const getOrganizationSummary = async (organizationId) => {
  try {
    return await axios.get(`/organizations/${organizationId}/summary/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * List org membership roles.
 *
 * @param {object} payload - { organizationId, sorting, pagination, searchTerm }
 * @returns {Promise<object>} Paginated org roles
 */
export const getOrganizationRoles = async ({
  organizationId,
  sorting,
  pagination,
  searchTerm,
}) => {
  const params = configureRequestParams(sorting, pagination, searchTerm)
  try {
    return await axios.get(`/organizations/${organizationId}/roles/${params}&expand=user`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Add an org membership role (org owners/admins; Xverse admins).
 *
 * @param {object} payload - { organizationId, ...roleData }
 * @returns {Promise<object>} The created org role
 */
export const addOrganizationRole = async ({ organizationId, ...data }) => {
  try {
    return await axios.post(`/organizations/${organizationId}/roles/`, data)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Update an org membership role (change Owner/Admin). Replaces the role set.
 *
 * @param {object} payload - { organizationId, id, roles }
 * @returns {Promise<object>} The updated org role
 */
export const updateOrganizationRole = async ({ organizationId, id, ...data }) => {
  try {
    return await axios.patch(`/organizations/${organizationId}/roles/${id}/`, data)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Remove a user's org membership role (does not touch their facility access).
 *
 * @param {object} payload - { organizationId, id }
 */
export const deleteOrganizationRole = async ({ organizationId, id }) => {
  try {
    return await axios.delete(`/organizations/${organizationId}/roles/${id}/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Unified, org-scoped users list: everyone with access to the org (via a
 * facility role or an org role), with their org-scoped facility access.
 * Supports `facility` (entity id) and `role` (org role) filters (RSCX-365).
 *
 * @param {object} payload - { organizationId, sorting, pagination, searchTerm, filters }
 * @returns {Promise<object>} Paginated users
 */
export const getOrganizationUsers = async ({
  organizationId,
  sorting,
  pagination,
  searchTerm,
  filters,
}) => {
  const params = configureRequestParams(sorting, pagination, searchTerm, filters)
  try {
    return await axios.get(`/organizations/${organizationId}/users/${params}`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

// ------------------------------ Accounts ------------------------------

/**
 * List the accounts (facilities) an organization owns, with org-scoped CRUD.
 *
 * @param {object} payload - { organizationId, sorting, pagination, searchTerm }
 * @returns {Promise<object>} Paginated accounts
 */
export const getOrganizationAccounts = async ({
  organizationId,
  sorting,
  pagination,
  searchTerm,
}) => {
  const params = configureRequestParams(sorting, pagination, searchTerm)
  try {
    return await axios.get(`/organizations/${organizationId}/accounts/${params}`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Create an account under an organization (auto-linked to the org).
 *
 * @param {object} payload - { organizationId, ...accountData }
 * @returns {Promise<object>} The created account
 */
export const addOrganizationAccount = async ({ organizationId, ...data }) => {
  try {
    return await axios.post(`/organizations/${organizationId}/accounts/`, data)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Update an org-owned account.
 *
 * @param {object} payload - { organizationId, id, ...accountData }
 * @returns {Promise<object>} The updated account
 */
export const updateOrganizationAccount = async ({ organizationId, id, ...data }) => {
  try {
    return await axios.patch(`/organizations/${organizationId}/accounts/${id}/`, data)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Delete an org-owned account (cascades to its data).
 *
 * @param {object} payload - { organizationId, id }
 */
export const deleteOrganizationAccount = async ({ organizationId, id }) => {
  try {
    return await axios.delete(`/organizations/${organizationId}/accounts/${id}/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

// ------------------------------ Shared jobs ------------------------------

/**
 * List an org's shared job titles.
 *
 * @param {object} payload - { organizationId, sorting, pagination, searchTerm }
 * @returns {Promise<object>} Paginated jobs
 */
export const getOrganizationJobs = async ({ organizationId, sorting, pagination, searchTerm }) => {
  const params = configureRequestParams(sorting, pagination, searchTerm)
  try {
    return await axios.get(`/organizations/${organizationId}/jobs/${params}`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Create a shared job for an org (propagates read-only to its facilities).
 *
 * @param {object} payload - { organizationId, title, description }
 * @returns {Promise<object>} The created job
 */
export const addOrganizationJob = async ({ organizationId, ...data }) => {
  try {
    return await axios.post(`/organizations/${organizationId}/jobs/`, data)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Update an org's shared job.
 *
 * @param {object} payload - { organizationId, id, title, description }
 * @returns {Promise<object>} The updated job
 */
export const updateOrganizationJob = async ({ organizationId, id, ...data }) => {
  try {
    return await axios.patch(`/organizations/${organizationId}/jobs/${id}/`, data)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Delete an org's shared job (removes it from every facility).
 *
 * @param {object} payload - { organizationId, id }
 */
export const deleteOrganizationJob = async ({ organizationId, id }) => {
  try {
    return await axios.delete(`/organizations/${organizationId}/jobs/${id}/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

// ------------------------------ Shared content ------------------------------

/**
 * List org-shared courses (org-level management view).
 *
 * @param {object} payload - { organizationId, sorting, pagination, searchTerm, filters }
 * @returns {Promise<object>} Paginated org courses
 */
export const getOrganizationCourses = async ({
  organizationId,
  sorting,
  pagination,
  searchTerm,
  filters,
}) => {
  const params = configureRequestParams(sorting, pagination, searchTerm, filters)
  try {
    return await axios.get(`/organizations/${organizationId}/courses/${params}`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * List org-shared certificates (org-level management view).
 *
 * @param {object} payload - { organizationId, sorting, pagination, searchTerm, filters }
 * @returns {Promise<object>} Paginated org certificates
 */
export const getOrganizationCertificates = async ({
  organizationId,
  sorting,
  pagination,
  searchTerm,
  filters,
}) => {
  const params = configureRequestParams(sorting, pagination, searchTerm, filters)
  try {
    return await axios.get(`/organizations/${organizationId}/certificates/${params}`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * List org EAP templates.
 *
 * @param {object} payload - { organizationId, sorting, pagination, searchTerm }
 * @returns {Promise<object>} Paginated EAP templates
 */
export const getOrganizationEAPTemplates = async ({
  organizationId,
  sorting,
  pagination,
  searchTerm,
}) => {
  const params = configureRequestParams(sorting, pagination, searchTerm)
  try {
    return await axios.get(`/organizations/${organizationId}/eap-templates/${params}`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Get a single org EAP template.
 *
 * @param {object} payload - { organizationId, eapId }
 * @returns {Promise<object>} The EAP template
 */
export const getOrganizationEAPTemplate = async ({ organizationId, eapId }) => {
  try {
    return await axios.get(`/organizations/${organizationId}/eap-templates/${eapId}/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Create an org EAP template.
 *
 * @param {object} payload - { organizationId, ...templateData }
 * @returns {Promise<object>} The created template
 */
export const addOrganizationEAPTemplate = async ({ organizationId, ...data }) => {
  try {
    return await axios.post(`/organizations/${organizationId}/eap-templates/`, data)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Update an org EAP template.
 *
 * @param {object} payload - { organizationId, id, ...templateData }
 * @returns {Promise<object>} The updated template
 */
export const updateOrganizationEAPTemplate = async ({ organizationId, id, ...data }) => {
  try {
    return await axios.patch(`/organizations/${organizationId}/eap-templates/${id}/`, data)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Delete an org EAP template.
 *
 * @param {object} payload - { organizationId, id }
 */
export const deleteOrganizationEAPTemplate = async ({ organizationId, id }) => {
  try {
    return await axios.delete(`/organizations/${organizationId}/eap-templates/${id}/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Org-wide calendar: events aggregated across child facilities.
 *
 * @param {object} payload - { organizationId, filters }
 * @returns {Promise<object>} Paginated events
 */
export const getOrganizationEvents = async ({ organizationId, filters }) => {
  const params = configureRequestParams([], {}, null, filters)
  try {
    return await axios.get(`/organizations/${organizationId}/events/${params}`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Org-wide enrollment/certification records across child facilities.
 *
 * @param {object} payload - { organizationId, sorting, pagination, searchTerm, filters }
 * @returns {Promise<object>} Paginated student certificates
 */
export const getOrganizationStudentCertificates = async ({
  organizationId,
  sorting,
  pagination,
  searchTerm,
  filters,
}) => {
  const params = configureRequestParams(sorting, pagination, searchTerm, filters)
  try {
    return await axios.get(`/organizations/${organizationId}/student-certificates/${params}`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

// ----------------------------- Shared-content assignment -----------------------------
// The org decides which facilities a shared EAP / custom course reaches:
// `assignedToAll` covers every facility (and any added later), otherwise it goes
// only to `facilityIds`. Assigned facilities see it read-only; org edits propagate.

/**
 * Get which facilities a shared org EAP is assigned to.
 *
 * @param {object} payload - { organizationId, eapId }
 * @returns {Promise<object>} { assignedToAll, facilityIds }
 */
export const getOrganizationEAPAssignment = async ({ organizationId, eapId }) => {
  try {
    return await axios.get(`/organizations/${organizationId}/eap-templates/${eapId}/assignment/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Set which facilities a shared org EAP is assigned to (replaces the set).
 *
 * @param {object} payload - { organizationId, eapId, assignedToAll, facilityIds }
 * @returns {Promise<object>} The saved assignment
 */
export const setOrganizationEAPAssignment = async ({
  organizationId,
  eapId,
  assignedToAll,
  facilityIds,
}) => {
  try {
    return await axios.put(`/organizations/${organizationId}/eap-templates/${eapId}/assignment/`, {
      assignedToAll,
      facilityIds,
    })
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Get which facilities a shared org custom course (certificate) is assigned to.
 *
 * @param {object} payload - { organizationId, certificateId }
 * @returns {Promise<object>} { assignedToAll, facilityIds }
 */
export const getOrganizationCertificateAssignment = async ({ organizationId, certificateId }) => {
  try {
    return await axios.get(
      `/organizations/${organizationId}/certificates/${certificateId}/assignment/`,
    )
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Set which facilities a shared org custom course is assigned to (replaces the set).
 *
 * @param {object} payload - { organizationId, certificateId, assignedToAll, facilityIds }
 * @returns {Promise<object>} The saved assignment
 */
export const setOrganizationCertificateAssignment = async ({
  organizationId,
  certificateId,
  assignedToAll,
  facilityIds,
}) => {
  try {
    return await axios.put(
      `/organizations/${organizationId}/certificates/${certificateId}/assignment/`,
      { assignedToAll, facilityIds },
    )
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}
